import React, { useCallback, useEffect, useState } from "react";
import Loading from "../../../../components/Loading";
import DropdownSearch from "../../../../components/DropdownSearch";
import InputText from "../../../../components/InputText";
import TextArea from "../../../../components/TextArea";
import copiar_icon from "../../../../assets/vw/svg/copiar.svg";

function Endpoints({ app, urlApi, servicio }) {
    const [cargando, setCargando] = useState(false);
    const [endpoints, setEndpoints] = useState([]);
    const [endpointSeleccionado, setEndpointSeleccionado] = useState("");
    const [body, setBody] = useState("{}");

    const [resultado, setResultado] = useState(null);
    const [status, setStatus] = useState(null);
    const [duracion, setDuracion] = useState(null);
    const [error, setError] = useState("");

    const endpointActual = endpoints.find(
        (item) => item.nombre === endpointSeleccionado
    );

    // GET: obtener el catálogo de endpoints.
    const cargarEndpoints = useCallback(async () => {
        setCargando(true);
        setError("");
        setEndpoints([]);
        setEndpointSeleccionado("");
        setBody("{}");
        setResultado(null);
        setStatus(null);
        setDuracion(null);

        try {
            if (!urlApi) {
                throw new Error(
                    `No está configurada la URL de la aplicación ${app}.`
                );
            }

            const response = await fetch(
                `${urlApi.replace(/\/+$/, "")}/soporte/endpoints`,
                {
                    method: "GET",
                    headers: {
                        Authorization: "Bearer " + servicio.getToken(),
                        Accept: "application/json",
                    },
                }
            );

            const texto = await response.text();
            let data;

            try {
                data = texto ? JSON.parse(texto) : null;
            } catch {
                data = texto;
            }

            if (!response.ok) {
                throw new Error(
                    typeof data === "object" && data !== null
                        ? data.mensaje || `Error HTTP ${response.status}`
                        : `Error HTTP ${response.status}: ${texto}`
                );
            }

            if (!Array.isArray(data)) {
                throw new Error(
                    "El catálogo no devolvió una lista de endpoints."
                );
            }

            setEndpoints(data);

            if (data.length > 0) {
                setEndpointSeleccionado(data[0].nombre);
                setBody(
                    JSON.stringify(data[0].bodyEjemplo ?? {}, null, 2)
                );
            }
        } catch (err) {
            setError(err.message || "Error al consultar el catálogo.");
        } finally {
            setCargando(false);
        }
    }, [app, urlApi, servicio]);

    useEffect(() => {
        cargarEndpoints();
    }, [cargarEndpoints]);

    // Cambiar de endpoint y cargar sus parámetros de ejemplo.
    const seleccionarEndpoint = (nombre) => {
        const seleccionado = endpoints.find(
            (item) => item.nombre === nombre
        );

        setEndpointSeleccionado(nombre);
        setBody(
            JSON.stringify(seleccionado?.bodyEjemplo ?? {}, null, 2)
        );
        setResultado(null);
        setStatus(null);
        setDuracion(null);
        setError("");
    };

    // POST: probar el endpoint seleccionado.
    const probarEndpoint = async () => {
        if (!endpointActual) {
            setError("Selecciona un endpoint.");
            return;
        }

        let parametrosJson;

        try {
            parametrosJson = JSON.parse(body);

            if (
                parametrosJson === null ||
                Array.isArray(parametrosJson) ||
                typeof parametrosJson !== "object"
            ) {
                throw new Error("El JSON debe ser un objeto.");
            }
        } catch (err) {
            setError(err.message || "El body no contiene un JSON válido.");
            return;
        }

        setCargando(true);
        setError("");
        setResultado(null);
        setStatus(null);
        setDuracion(null);

        const inicio = Date.now();

        try {
            const response = await fetch(
                `${urlApi.replace(/\/+$/, "")}/soporte/endpoints`,
                {
                    method: "POST",
                    headers: {
                        Authorization: "Bearer " + servicio.getToken(),
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        nombre: endpointActual.nombre,
                        parametros: parametrosJson,
                    }),
                }
            );

            const texto = await response.text();
            let data;

            try {
                data = texto ? JSON.parse(texto) : null;
            } catch {
                data = texto;
            }

            setStatus(data?.data?.Status ? data.data.Status : response.status);
            setResultado(data);

            if (!response.ok) {
                setError(
                    `La petición respondió con HTTP ${response.status}.`
                );
            }
        } catch (err) {
            setError(
                err.message || "No fue posible conectar con el servicio."
            );
        } finally {
            setDuracion(Date.now() - inicio);
            setCargando(false);
        }
    };

    const copiar = async (texto) => {
        try {
            await navigator.clipboard.writeText(texto);
        } catch {
            setError("No fue posible copiar al portapapeles.");
        }
    };

    const colorStatus = (status) => {
        if (status >= 200 && status < 300) return "status-ok";
        if (status >= 400 && status < 500) return "status-warning";
        return "status-error";
    };

    const tooltipParametro = (parametro) => {
        const valores = endpointActual?.valoresPermitidos?.[parametro];

        if (!valores || Object.keys(valores).length === 0) return undefined;

        return Object.entries(valores)
            .map(([id, descripcion]) => `${id} = ${descripcion}`)
            .join("\n");
    };

    const textoResultado =
        resultado === null
            ? ""
            : typeof resultado === "string"
                ? resultado
                : JSON.stringify(resultado, null, 2);

    return (
        <>
            {cargando && <Loading />}
            <div className="endpoints-soporte endpoints-section">
                <div className="row">

                    {/* Columna request */}
                    <div className="col-lg-5">
                        <div style={{ marginTop: "27.5px" }}>
                            <DropdownSearch
                                menu={endpoints}
                                title={"Endpoints"}
                                defaultValue={endpointSeleccionado}
                                handleOptionSelected={(e) => {
                                    seleccionarEndpoint(e.props.value);
                                }}
                                fieldID={"nombre"}
                                fieldToShow={"nombre"}
                                extraStyle="w-100"
                            />
                        </div>

                        {endpointActual && (
                            <>
                                <div style={{ marginTop: "26px" }}>
                                    <InputText
                                        id={"ep-descripcion"}
                                        inputLabel={"Descripción"}
                                        inputField={endpointActual.descripcion || "---"}
                                        changingInput={() => { }}
                                        disabled
                                    />
                                </div>

                                <div className="d-flex align-items-start" style={{ marginTop: "37px", gap: "10px" }}>
                                    <div className="flex-grow-1 align-self-end">
                                        <InputText
                                            id={"ep-endpoint"}
                                            inputLabel={endpointActual.metodo || "—"}
                                            inputField={endpointActual.endpoint || ""}
                                            changingInput={() => { }}
                                            disabled
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="button_vw button_vw--copiar w-auto d-flex align-items-center justify-content-center"
                                        style={{ padding: "12px 13.7px" }}
                                        onClick={() => copiar(endpointActual.endpoint || "")}
                                        title="Copiar ruta del endpoint"
                                    >

                                        <img src={copiar_icon} alt="download" className="icono-copiar" />
                                        <span className={"ml-2"}>Copiar ruta</span>

                                    </button>
                                </div>

                                {endpointActual.parametros.map((p) => {
                                    const tooltip = tooltipParametro(p);

                                    return (
                                        <span
                                            key={p}
                                            className="parametros-soporte__chip"
                                            data-tooltip={tooltip ? `Valores permitidos:\n${tooltip}` : undefined}
                                        >
                                            {p}
                                        </span>
                                    );
                                })}
                                <div className="mb-3" style={{ marginTop: "26px" }}>
                                    <TextArea
                                        id="ep-body"
                                        inputLabel={"Body (JSON)"}
                                        inputField={body}
                                        changingInput={(e) => setBody(e.target.value)}
                                        extraStyle="font-monospace"
                                        rows={10}
                                        disabled={cargando}
                                    />
                                </div>

                                <button
                                    type="button"
                                    className="button_vw button_vw--primary order-1 order-sm-2"
                                    onClick={probarEndpoint}
                                    disabled={cargando}
                                >
                                    Consultar
                                </button>
                            </>
                        )}
                    </div>

                    {/* Columna response */}
                    <div className="col-lg-7">
                        <div className="respuesta-soporte sticky-lg-top">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-3">
                                    <span className="respuesta-soporte__titulo">Respuesta del servidor</span>

                                    {status !== null && (
                                        <span className={`status-soporte ${colorStatus(status)}`}>
                                            HTTP {status}
                                        </span>
                                    )}

                                    {duracion !== null && <small>{duracion} ms</small>}
                                </div>

                                {resultado !== null && (
                                    <button
                                        type="button"
                                        className="button_vw button_vw--copiar w-auto d-flex align-items-center justify-content-center"
                                        style={{ padding: "11px 10px" }}
                                        onClick={() => copiar(textoResultado)}
                                    >
                                        <img src={copiar_icon} alt="copiar" className="icono-copiar" />
                                        <span className="ms-2">Copiar respuesta</span>
                                    </button>
                                )}
                            </div>

                            <pre className="respuesta-soporte__resultado">{textoResultado}</pre>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
}

export default Endpoints;